import * as _ from 'lodash-es';
import attachmentSizeExceededToast from '../editor/attachmentSizeExceededToast';
import isFileOverlarge from './network/isFileOverlarge';
import uploadPostAttachment from './network/uploadPostAttachment';
import type { AttachmentFile } from '../editor/formats/Attachment';
import type Attachment from '../editor/formats/Attachment';
import type AttachmentMention from '../editor/formats/AttachmentMention';
import Queue from '../editor/utils/Queue';
import showEditorToast from '../editor/utils/showEditorToast';

const UPLOADING_PREFIX = 'UPLOADING:';

export const isAttachmentUploaded = ({ source }: AttachmentFile) =>
  !source.startsWith(UPLOADING_PREFIX);

export const filterValidFiles = (files: File[]) => {
  const [overlargeFiles, validFiles] = _.partition(files, isFileOverlarge);
  if (overlargeFiles.length) {
    overlargeFiles.forEach(() => {
      showEditorToast(attachmentSizeExceededToast);
    });
  }
  return validFiles;
};

class AttachmentUploader {
  // Remote attachment updates will recreate blots so we need to keep track all attachment blots
  // so that once an uploading is finished, we can find the related blot
  // even if it and the underlying DOM node has been recreated.
  private activeAttachmentBlots = new Set<Attachment | AttachmentMention>();
  private uploadQueue: Queue<string | null> = new Queue();
  private uploadClientId = `${UPLOADING_PREFIX}${Math.random()
    .toString(36)
    .substring(2, 10)}`;
  private fileIdMap = new WeakMap();

  upload(file: File, callback: (result: string | null | undefined) => void) {
    this.uploadQueue
      .enqueue(() => uploadPostAttachment(file))
      .then(callback)
      .catch((err) => {
        callback(null);

        const isPayloadTooLarge =
          err.networkError && err.networkError.statusCode === 413;

        if (isPayloadTooLarge) {
          showEditorToast(attachmentSizeExceededToast);
        } else {
          showEditorToast({
            level: 'error',
            message: 'Attachment could not be uploaded.',
          });
        }
      });
  }

  uploadFiles(files: File[]) {
    files.forEach((file) => {
      this.upload(file, (source) => {
        this.activeAttachmentBlots.forEach((blot) => {
          blot.cleanUpload(file, source);
        });
      });
    });
  }

  addActiveAttachmentBlot(blot: Attachment | AttachmentMention) {
    this.activeAttachmentBlots.add(blot);
  }

  removeActiveAttachmentBlot(blot: Attachment | AttachmentMention) {
    this.activeAttachmentBlots.delete(blot);
  }

  shouldShowUploadingAttachment({ source }: AttachmentFile) {
    return source.startsWith(this.uploadClientId);
  }

  getIdForFile(file: File) {
    if (!this.fileIdMap.has(file)) {
      const id = _.uniqueId(this.uploadClientId);
      this.fileIdMap.set(file, id);
    }
    return this.fileIdMap.get(file);
  }

  normalizeToAttachmentFiles(
    value: (AttachmentFile | File)[],
  ): AttachmentFile[] {
    return value.map((item) =>
      item instanceof File
        ? { source: this.getIdForFile(item), size: item.size, name: item.name }
        : item,
    );
  }
}

export default new AttachmentUploader();
